import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32 } from "./limits.ts";

/**
 * The `thingEvent` frame (ADR-096 decision 22, PROTOCOL.md 5.17): server to client, events on things in view that are
 * no change of state, a shrine's pulse today. After the header: a `u32` tick, a `u8` count of 1 to 16, then 3 bytes an
 * event, a `u16` handle and a `u8` event code. A map server writes it into a buffer it makes once
 * (`putThingEventEntry`, `putThingEventHeader`), so a send allocates nothing; the decoder fills a record made once,
 * allocates nothing, never throws and never reads past the frame (ADR-030).
 */

/**
 * Bytes before the first event: the frame header, the tick and the count.
 */
export const THING_EVENT_HEADER_BYTES = HEADER_BYTES + 5;

/**
 * Bytes of one event: the thing's handle and the event code.
 */
export const THING_EVENT_ENTRY_BYTES = 3;

/**
 * The most events one frame carries.
 */
export const MAX_THING_EVENTS = 16;

/**
 * The event codes this build knows. A page skips a code it does not know, so a later server may add one.
 */
export const ThingEventCode = { Pulse: 1 } as const;
export type ThingEventCode = (typeof ThingEventCode)[keyof typeof ThingEventCode];

const COUNT_OFFSET = HEADER_BYTES + 4;
const MAX_BYTE = 255;

/**
 * One event to encode: the thing's handle and the event code.
 */
export interface ThingEventEntry {
  readonly handle: number;
  readonly event: number;
}

/**
 * A decoded `thingEvent`, reused across frames: the tick, how many events the frame held, and their handles and
 * codes, the first `count` of each valid.
 */
export interface ThingEventRecord {
  tick: number;
  count: number;
  readonly handles: Uint16Array;
  readonly events: Uint8Array;
}

/**
 * A fresh record for `decodeThingEvent` to fill, with room for the most events a frame carries.
 *
 * @returns An empty record.
 */
export function newThingEventRecord(): ThingEventRecord {
  return { tick: 0, count: 0, handles: new Uint16Array(MAX_THING_EVENTS), events: new Uint8Array(MAX_THING_EVENTS) };
}

/**
 * Bytes of a frame of a number of events.
 *
 * @param count The events.
 * @returns `8 + 3 * count`.
 */
export function thingEventBytes(count: number): number {
  return THING_EVENT_HEADER_BYTES + THING_EVENT_ENTRY_BYTES * count;
}

/**
 * Writes one event at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths, as a map server does with handles it holds in typed arrays of that width.
 *
 * @param buffer The frame.
 * @param index The event's place, from 0.
 * @param handle The thing's handle.
 * @param event The event code.
 */
export function putThingEventEntry(buffer: Uint8Array, index: number, handle: number, event: number): void {
  const at = THING_EVENT_HEADER_BYTES + THING_EVENT_ENTRY_BYTES * index;
  putU16(buffer, at, handle);
  buffer[at + 2] = event;
}

/**
 * Writes a frame's header once its events are in place.
 *
 * @param buffer The frame.
 * @param tick The server tick, a `u32`.
 * @param count The events written, 1 to 16.
 * @returns The frame's length in bytes.
 */
export function putThingEventHeader(buffer: Uint8Array, tick: number, count: number): number {
  putHeader(buffer, MessageType.ThingEvent);
  putU32(buffer, HEADER_BYTES, tick);
  buffer[COUNT_OFFSET] = count;
  return thingEventBytes(count);
}

/**
 * Encodes a `thingEvent` into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `thingEventBytes(events.length)` long.
 * @param tick The server tick.
 * @param events 1 to 16 events.
 * @returns Bytes written.
 * @throws Error when there are no events or more than 16, the buffer is too small, or a field does not fit its width.
 */
export function encodeThingEvent(buffer: Uint8Array, tick: number, events: readonly ThingEventEntry[]): number {
  if (events.length < 1 || events.length > MAX_THING_EVENTS) {
    throw new Error(`thingEvent carries 1 to ${MAX_THING_EVENTS} events, not ${events.length}`);
  }
  const length = thingEventBytes(events.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte thingEvent`);
  }
  assertField(tick, MAX_U32, `thingEvent tick ${tick}`);
  for (let index = 0; index < events.length; index += 1) {
    const entry = events[index] as ThingEventEntry;
    assertField(entry.handle, MAX_U16, `thingEvent handle ${entry.handle}`);
    assertField(entry.event, MAX_BYTE, `thingEvent event ${entry.event}`);
    putThingEventEntry(buffer, index, entry.handle, entry.event);
  }
  return putThingEventHeader(buffer, tick, events.length);
}

/**
 * Decodes a `thingEvent` frame into a record. Never throws and reads no byte past the frame: a frame shorter than its
 * header, a count of 0 or past 16, a length other than `8 + 3 * count` or another type is malformed, and another
 * version a mismatch, each leaving the record untouched. Every tick, handle and event code is well formed; a code
 * this build does not know is decoded as it is, for the page to skip.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodeThingEvent(frame: Uint8Array, out: ThingEventRecord): DecodeVerdict {
  if (frame.length < THING_EVENT_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[COUNT_OFFSET] as number;
  if (count < 1 || count > MAX_THING_EVENTS) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.ThingEvent, thingEventBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  for (let index = 0; index < count; index += 1) {
    const at = THING_EVENT_HEADER_BYTES + THING_EVENT_ENTRY_BYTES * index;
    out.handles[index] = readU16(frame, at);
    out.events[index] = frame[at + 2] as number;
  }
  out.tick = readU32(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
